import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCommonTranslation } from '../../modules/i18n/hooks/useTranslation';

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  dropdownClassName?: string;
  disabled?: boolean;
}

const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  dropdownClassName = '',
  disabled = false
}) => {
  const { t } = useCommonTranslation();
  const resolvedPlaceholder = placeholder ?? t('actions.select');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (isOpen) {
        const clickedButton = buttonRef.current?.contains(target);
        const clickedDropdown = dropdownRef.current?.contains(target);
        if (!clickedButton && !clickedDropdown) {
          setIsOpen(false);
        }
      }
    };

    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      updatePosition();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      const newState = !isOpen;
      setIsOpen(newState);
      if (newState && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <button
        ref={buttonRef}
        type='button'
        onClick={handleToggle}
        disabled={disabled}
        className={`
          flex w-full items-center justify-between rounded-lg border border-gray-300
          bg-white px-4 py-2 text-left shadow-sm
          text-gray-900 transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20
          focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text
          ${disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:border-gray-400 hover:shadow-md dark:hover:border-border-dark'
          }
          ${isOpen ? 'border-primary ring-2 ring-primary shadow-md' : ''}
        `}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {selectedOption?.icon && <span className="flex-shrink-0">{selectedOption.icon}</span>}
          <span className={`whitespace-nowrap overflow-hidden text-ellipsis ${selectedOption ? 'font-medium' : 'text-gray-500 dark:text-dark-text-secondary'}`}>
            {selectedOption ? selectedOption.label : resolvedPlaceholder}
          </span>
        </div>
        <svg
          className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : 'text-gray-400'}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed bg-white dark:bg-dark-background-secondary rounded-lg shadow-xl border border-gray-200 dark:border-border-dark z-[999999] max-h-64 overflow-auto animate-in fade-in slide-in-from-top-2 duration-200 ${dropdownClassName}`}
          style={dropdownPosition}
        >
          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-dark-text-secondary text-center">
              No options available
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type='button'
                onClick={() => handleSelect(option.value)}
                className={`
                  flex items-center space-x-3 w-full px-4 py-2 text-left text-sm transition-all duration-150
                  ${value === option.value
                    ? 'bg-primary text-white font-medium'
                    : 'text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-background-hover'}
                  first:rounded-t-lg last:rounded-b-lg
                `}
              >
                {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                <span className="truncate flex-1">{option.label}</span>
                {value === option.value && (
                  <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>,
        (document.fullscreenElement as HTMLElement) || document.body
      )}
    </div>
  );
};

export default Select;
